import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { InputAdornment } from '@mui/material';
import { IBuilding } from 'spw-redux/modules/projects/types';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from 'spw-theme';
import {
  setSubtotalBuildingMultiplier,
  setSubtotalProgramMultiplier,
  setRsfLoadFactor,
} from 'spw-redux/modules/calculators/actions';
import { asUsf, asUsfOrZero, handleOnKeyPress } from 'spw-shared/utils';
import {
  getRsfProgramCalculationLabels,
  getProgramCalculationLabels,
  GROWTH_CALCULATORS,
  NO_DATA_TO_SHOW,
} from 'spw-constants';
import { convertToTwoDec } from 'spw-components/growth-calculator/utils';
import { Label } from 'spw-components/growth-calculator/styles';
import EditIcon from '@mui/icons-material/Edit';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import TotalProgramFactorModal from 'spw-components/dialogs/total-program/total-program-factor-modal';
import DepartmentsRsfFactorModal from 'spw-components/dialogs/departments-rsf-factor-modal';
import UsfEditableRow from 'spw-components/programming-scenarios/building-hs-sc-scenario/usf-editable-row';
import { SPWButtonIconAndText } from 'spw-shared/globalComponents';
import Img from 'react-cool-img';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import ActionsDialog from 'spw-components/dialogs/actions-dialog';
import { capitalize } from 'lodash';
import ReadOnlyRow from './readOnlyRow';
import {
  XSmallSPWInput,
  RowWithTopMargin,
  BlackText,
  SignColumn,
  TextColumn,
  GreyText,
  USFCustomDivider,
  Row10TopPadding,
  TitleText,
  OperationText,
  FlexEndColumn,
  FactorInput,
  AddOrRemoveRsfBtnContainer,
} from './styles';
import { PlainProgramScenario } from '../../../models/Scenario';
import { SummaryCalculator } from '../../../models/ProgramSummaryCalculator';

interface IUSFBuildingCalculationsLeftPanel {
  building: IBuilding | null;
  scenario: PlainProgramScenario;
  programSummaryCalculator: SummaryCalculator;
  scenarios?: PlainProgramScenario[];
  showDecimals: boolean;
  inBrowserDownload?: boolean;
}

const USFBuildingCalculationsLeftPanel: React.FC<IUSFBuildingCalculationsLeftPanel> = ({
  building,
  programSummaryCalculator,
  scenarios,
  scenario,
  showDecimals,
  inBrowserDownload,
}) => {
  if (!scenario || !programSummaryCalculator) return null;

  const [editValue, setEditValue] = useState('');
  const { getUnit, convertToUnit } = useUnitConversion();
  const [editFactor, setEditFactor] = useState('');
  const [showTPFactorModal, setShowTPFactorModal] = useState(false);
  const [showDepsRsfFactorModal, setShowDepsRsfFactorModal] = useState(false);
  const [rsfVisible, setRsfVisibility] = useState(false);
  const [removeRsfModalVisible, setRemoveRsfModalVisibility] = useState(false);
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const { getUnitName } = useUnitConversion();

  const dispatch = window.SPWExport ? Function.prototype : useDispatch();
  const {
    isRsfLoadFactorSetByUser,
    totalAvailableUSF,
    totalAvailableRSF,
    calculatorType,
  } = scenario;

  const isMacroCalculator =
    calculatorType === GROWTH_CALCULATORS.macroProgramming;
  const isTotalProgram = calculatorType === GROWTH_CALCULATORS.totalProgram;
  const isDepartmentScenario =
    calculatorType === GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments;

  const {
    totalProgramUSF,
    totalProgramRSF,
    circulationPercentage,
    circulationUSF,
    subtotalMultiplierForCirculation,
    subtotalProgramWithCirculationUSF,
    subtotalProgramUSF,
    deltaProgramUSF,
    deltaProgramRSF,
    planningFactorPercentage,
    additionalUSFForPlanningFactor,
    rsfLoadFactorPercentage,
    rentableLoadFactor,
  } = programSummaryCalculator || {};

  useEffect(() => {
    setRsfVisibility(isRsfLoadFactorSetByUser);
  }, [isRsfLoadFactorSetByUser]);

  const handleMultiplierInputChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    setEditValue(convertToTwoDec(inputValue));
  };

  const handleMultiplierInputBlur = () => {
    if (editValue === '' || parseFloat(editValue) === 0) {
      dispatch(setSubtotalBuildingMultiplier(0));
    } else if (parseFloat(editValue) > 3) {
      dispatch(setSubtotalBuildingMultiplier(3));
    } else {
      dispatch(setSubtotalBuildingMultiplier(parseFloat(editValue)));
    }
    setEditFactor('');
    setEditValue('');
  };

  const handlePlanningFactorInputChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    setEditValue(convertToTwoDec(inputValue));
  };

  const handleRsfLoadFactorInputChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    setEditValue(convertToTwoDec(inputValue));
  };

  const handlePlanningFactorOnBlur = () => {
    if (editValue === '' || parseFloat(editValue) === 0) {
      dispatch(setSubtotalProgramMultiplier(0));
    } else if (parseFloat(editValue) > 99) {
      dispatch(setSubtotalProgramMultiplier(99));
    } else {
      dispatch(setSubtotalProgramMultiplier(parseFloat(editValue)));
    }
    setEditFactor('');
    setEditValue('');
  };

  const handleRsfLoadFactorOnBlur = useCallback(() => {
    if (editValue === '' || parseFloat(editValue) === 0) {
      dispatch(setRsfLoadFactor(0));
    } else if (parseFloat(editValue) > 75) {
      dispatch(setRsfLoadFactor(0.75));
    } else {
      dispatch(
        setRsfLoadFactor(parseFloat((parseFloat(editValue) / 100).toFixed(4)))
      );
    }
    setEditFactor('');
    setEditValue('');
  }, [editValue, dispatch]);

  const titleLabel = (label: string) => {
    const splitTitle = label.split(' ');
    const [firstWord, ...restOfWords] = splitTitle;
    return (
      <>
        <div>{firstWord}</div>
        <div>{restOfWords.join(' ')}</div>
      </>
    );
  };

  const showTPSCirculationMultiplierDialog = useCallback(
    (e: MouseEvent<SVGSVGElement>) => {
      e.preventDefault();
      setEditFactor('multiplier');
      setShowTPFactorModal(true);
    },
    []
  );

  const showTPSPlanningFactorDialog = useCallback(
    (e: MouseEvent<SVGSVGElement>) => {
      e.preventDefault();
      setEditFactor('planning-factor');
      setShowTPFactorModal(true);
    },
    []
  );

  const showDepsRsfFactorDialog = useCallback(
    (e: MouseEvent<SVGSVGElement>) => {
      e.preventDefault();
      setEditFactor('rsf-load-factor');
      setShowDepsRsfFactorModal(true);
    },
    []
  );

  const hasSubtotalProgramUSF = subtotalProgramUSF && subtotalProgramUSF > 0;

  const planningFactorValue =
    editFactor === 'planning-factor'
      ? editValue
      : planningFactorPercentage?.toFixed(2);

  const rsfLoadFactorValue =
    editFactor === 'rsf-load-factor'
      ? editValue
      : rsfLoadFactorPercentage?.toFixed(2);

  const useDepartmentsRsfInput = isDepartmentScenario || isTotalProgram;

  return building || isMacroCalculator ? (
    <>
      {isTotalProgram && (
        <TotalProgramFactorModal
          scenarios={scenarios}
          totalProgram={scenario}
          summaryCalculator={programSummaryCalculator}
          editFactor={editFactor}
          setEditFactor={setEditFactor}
          showDialog={showTPFactorModal}
          setShowDialog={setShowTPFactorModal}
        />
      )}

      {useDepartmentsRsfInput && (
        <DepartmentsRsfFactorModal
          showDialog={showDepsRsfFactorModal}
          setShowDialog={(show) => setShowDepsRsfFactorModal(show)}
          setEditFactor={setEditFactor}
          summaryCalculator={programSummaryCalculator}
        />
      )}

      <ActionsDialog
        containerClassName="smaller"
        isOpen={removeRsfModalVisible}
        title="Remove Rentable Load Factor"
        description={`All Rentable Square ${capitalize(
          getUnitName('main-unit')
        )} calculations in this scenario will be deleted when the Rentable Load Factor is removed.`}
        actions={[
          {
            text: 'Remove',
            onClick: () => {
              dispatch(setRsfLoadFactor(null));
              setRsfVisibility(false);
              setRemoveRsfModalVisibility(false);
            },
            variant: 'delete',
            testingId: 'remove-rsf-dialog-delete',
          },
          {
            text: 'CANCEL',
            onClick: () => setRemoveRsfModalVisibility(false),
            variant: 'cancel',
          },
        ]}
      />

      <RowWithTopMargin container>
        <FlexEndColumn item xs={3}>
          <TitleText data-testid="summary_calcs_row1_left_label">
            {titleLabel(
              getProgramCalculationLabels('subtotal').replace(
                'USF',
                getUnit('area')
              )
            )}
          </TitleText>
          <TextColumn>
            <BlackText data-testid="summary_calcs_row1_left_value">
              {hasSubtotalProgramUSF
                ? convertToUnit(subtotalProgramUSF, {
                    unitType: 'area',
                    formatter: (v) => asUsf(v, showDecimals),
                  })
                : NO_DATA_TO_SHOW}
            </BlackText>
          </TextColumn>
        </FlexEndColumn>
        <FlexEndColumn item xs={1}>
          <SignColumn className="input">
            <OperationText> x </OperationText>
          </SignColumn>
        </FlexEndColumn>
        <FlexEndColumn item xs={3}>
          <Label data-testid="summary_calcs_row1_middle_label">
            Multiplier
          </Label>
          {isTotalProgram ? (
            <FactorInput
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              value={subtotalMultiplierForCirculation?.toFixed(2)}
              disabled={currentUserIsViewOnly}
              data-testid="summary_calcs_row1_middle_value"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <EditIcon
                      onClick={
                        currentUserIsViewOnly
                          ? undefined
                          : showTPSCirculationMultiplierDialog
                      }
                      key="edit"
                      data-testid="edit_circulation_multiplier"
                      sx={{ color: colors.mainBlue }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <XSmallSPWInput
              style={{
                paddingTop: window.SPWExport ? '3px' : '0',
                height: window.SPWExport ? '35px' : '0',
              }}
              disabled={currentUserIsViewOnly}
              onFocus={() => {
                setEditFactor('multiplier');
                setEditValue(
                  subtotalMultiplierForCirculation === 0
                    ? ''
                    : subtotalMultiplierForCirculation.toString() || ''
                );
              }}
              value={
                editFactor === 'multiplier'
                  ? editValue
                  : subtotalMultiplierForCirculation?.toFixed(2)
              }
              onKeyPress={handleOnKeyPress}
              onBlur={handleMultiplierInputBlur}
              onChange={handleMultiplierInputChange}
              data-testid="summary_calcs_row1_middle_value"
            />
          )}
        </FlexEndColumn>
        <FlexEndColumn item xs={1}>
          <SignColumn className="input">
            <OperationText> = </OperationText>
          </SignColumn>
        </FlexEndColumn>
        <FlexEndColumn item xs={3}>
          <TitleText data-testid="summary_calcs_row1_right_label">
            {Math.round(circulationPercentage)}% Circulation
          </TitleText>
          <TextColumn>
            <BlackText data-testid="summary_calcs_row1_right_value">
              {hasSubtotalProgramUSF
                ? convertToUnit(circulationUSF, {
                    unitType: 'area',
                    formatter: (v) => asUsfOrZero(v as number, showDecimals),
                  })
                : NO_DATA_TO_SHOW}
            </BlackText>
          </TextColumn>
        </FlexEndColumn>
      </RowWithTopMargin>
      <USFCustomDivider />
      <ReadOnlyRow
        leftHeader={titleLabel(
          getProgramCalculationLabels('subtotal').replace(
            'USF',
            getUnit('area')
          )
        )}
        leftValue={
          hasSubtotalProgramUSF
            ? convertToUnit(subtotalProgramUSF, {
                unitType: 'area',
                formatter: (v) => asUsfOrZero(v, showDecimals),
              })
            : NO_DATA_TO_SHOW
        }
        leftSign="+"
        middleHeader="Circulation"
        middleValue={
          hasSubtotalProgramUSF
            ? convertToUnit(circulationUSF, {
                unitType: 'area',
                formatter: (v) => asUsfOrZero(v, showDecimals),
              })
            : NO_DATA_TO_SHOW
        }
        rightHeader="Subtotal Program With Circulation"
        rightValue={
          hasSubtotalProgramUSF
            ? convertToUnit(subtotalProgramWithCirculationUSF, {
                unitType: 'area',
              })
            : NO_DATA_TO_SHOW
        }
        testingIdPrefix="summary_calcs_row2"
        showDecimals={showDecimals}
      />
      <USFCustomDivider />
      <RowWithTopMargin container>
        <FlexEndColumn item xs={3}>
          <TitleText data-testid="summary_calcs_row3_left_label">
            Subtotal Program With Circulation
          </TitleText>
          <TextColumn>
            <BlackText data-testid="summary_calcs_row3_left_value">
              {hasSubtotalProgramUSF
                ? convertToUnit(subtotalProgramWithCirculationUSF, {
                    unitType: 'area',
                    formatter: (v) => asUsfOrZero(v, showDecimals),
                  })
                : NO_DATA_TO_SHOW}
            </BlackText>
          </TextColumn>
        </FlexEndColumn>
        <FlexEndColumn item xs={1}>
          <SignColumn className="input">
            <OperationText>x</OperationText>
          </SignColumn>
        </FlexEndColumn>
        <FlexEndColumn item xs={3}>
          <Label data-testid="summary_calcs_row3_middle_label">
            <div>Planning</div>
            <div>Factor %</div>
          </Label>
          {isTotalProgram ? (
            <FactorInput
              data-testid="summary_calcs_row3_middle_value"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              value={planningFactorPercentage?.toFixed(2)}
              disabled={currentUserIsViewOnly}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <EditIcon
                      onClick={
                        currentUserIsViewOnly
                          ? undefined
                          : showTPSPlanningFactorDialog
                      }
                      data-testid="edit_planning_factor"
                      key="edit"
                      sx={{ color: colors.mainBlue }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <XSmallSPWInput
              textAlign="right"
              value={planningFactorValue || ''}
              disabled={currentUserIsViewOnly}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" style={{ marginLeft: 0 }}>
                    <GreyText>%</GreyText>
                  </InputAdornment>
                ),
              }}
              onFocus={() => {
                setEditFactor('planning-factor');
                setEditValue(
                  planningFactorPercentage === 0
                    ? ''
                    : planningFactorPercentage?.toString() || ''
                );
              }}
              onKeyPress={handleOnKeyPress}
              onBlur={handlePlanningFactorOnBlur}
              onChange={handlePlanningFactorInputChange}
              data-testid="summary_calcs_row3_middle_value"
            />
          )}
        </FlexEndColumn>
        <FlexEndColumn item xs={1}>
          <SignColumn className="input">
            <OperationText> = </OperationText>
          </SignColumn>
        </FlexEndColumn>
        <FlexEndColumn item xs={3}>
          <TitleText data-testid="summary_calcs_row3_right_label">
            <div>Planning</div>
            <div>Factor</div>
          </TitleText>
          <TextColumn>
            <BlackText data-testid="summary_calcs_row3_right_value">
              {hasSubtotalProgramUSF
                ? convertToUnit(additionalUSFForPlanningFactor, {
                    unitType: 'area',
                    formatter: (v) => asUsfOrZero(v, showDecimals),
                  })
                : NO_DATA_TO_SHOW}
            </BlackText>
          </TextColumn>
        </FlexEndColumn>
      </RowWithTopMargin>
      <USFCustomDivider />
      <ReadOnlyRow
        leftHeader="Subtotal Program With Circulation"
        leftValue={
          hasSubtotalProgramUSF
            ? convertToUnit(subtotalProgramWithCirculationUSF, {
                unitType: 'area',
                formatter: (v) => asUsfOrZero(v, showDecimals),
              })
            : NO_DATA_TO_SHOW
        }
        leftSign="+"
        middleValue={
          hasSubtotalProgramUSF
            ? convertToUnit(additionalUSFForPlanningFactor, {
                unitType: 'area',
                formatter: (v) => asUsfOrZero(v, showDecimals),
              })
            : NO_DATA_TO_SHOW
        }
        middleHeader={
          <>
            <div>Planning</div>
            <div>Factor</div>
          </>
        }
        rightHeader={titleLabel(
          getProgramCalculationLabels('total').replace('USF', getUnit('area'))
        )}
        rightValue={
          hasSubtotalProgramUSF
            ? convertToUnit(totalProgramUSF, {
                unitType: 'area',
              })
            : NO_DATA_TO_SHOW
        }
        testingIdPrefix="summary_calcs_row4"
        showDecimals={showDecimals}
      />
      <USFCustomDivider />
      {rsfVisible && (
        <>
          <RowWithTopMargin className="gray-bg" container>
            <FlexEndColumn item xs={3}>
              <TitleText data-testid="summary_calcs_rsf_row_left_label">
                {titleLabel(
                  getProgramCalculationLabels('total').replace(
                    'USF',
                    getUnit('area')
                  )
                )}
              </TitleText>
              <TextColumn>
                <BlackText data-testid="summary_calcs_rsf_row_left_value">
                  {hasSubtotalProgramUSF
                    ? convertToUnit(totalProgramUSF, {
                        unitType: 'area',
                        formatter: (v) => asUsfOrZero(v, showDecimals),
                      })
                    : NO_DATA_TO_SHOW}
                </BlackText>
              </TextColumn>
            </FlexEndColumn>
            <FlexEndColumn item xs={1}>
              <SignColumn className="input">
                <OperationText>x</OperationText>
              </SignColumn>
            </FlexEndColumn>
            <FlexEndColumn item xs={3}>
              <Label data-testid="summary_calcs_rsf_row_middle_label">
                <div>Rentable</div>
                <div>Load Factor %</div>
              </Label>
              {useDepartmentsRsfInput ? (
                <FactorInput
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  value={rsfLoadFactorValue}
                  data-testid="summary_calcs_rsf_row_middle_value"
                  disabled={currentUserIsViewOnly}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <EditIcon
                          data-testid="edit_rsf_load_factor"
                          onClick={
                            currentUserIsViewOnly
                              ? undefined
                              : showDepsRsfFactorDialog
                          }
                          key="edit"
                          sx={{ color: colors.mainBlue }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <XSmallSPWInput
                  textAlign="right"
                  value={rsfLoadFactorValue || ''}
                  disabled={currentUserIsViewOnly}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" style={{ marginLeft: 0 }}>
                        <GreyText>%</GreyText>
                      </InputAdornment>
                    ),
                  }}
                  onFocus={() => {
                    setEditFactor('rsf-load-factor');
                    setEditValue(
                      rsfLoadFactorPercentage === 0
                        ? ''
                        : rsfLoadFactorPercentage?.toString() || ''
                    );
                  }}
                  onKeyPress={handleOnKeyPress}
                  onBlur={handleRsfLoadFactorOnBlur}
                  onChange={handleRsfLoadFactorInputChange}
                  data-testid="summary_calcs_rsf_row_middle_value"
                />
              )}
            </FlexEndColumn>
            <FlexEndColumn item xs={1}>
              <SignColumn className="input">
                <OperationText> = </OperationText>
              </SignColumn>
            </FlexEndColumn>
            <FlexEndColumn item xs={3}>
              <TitleText data-testid="summary_calcs_rsf_row_right_label">
                <div>Rentable</div>
                <div>Load Factor</div>
              </TitleText>
              <TextColumn>
                <BlackText data-testid="summary_calcs_rsf_row_right_value">
                  {hasSubtotalProgramUSF
                    ? convertToUnit(rentableLoadFactor, {
                        unitType: 'area',
                        formatter: (v) => asUsfOrZero(v, showDecimals),
                      })
                    : NO_DATA_TO_SHOW}
                </BlackText>
              </TextColumn>
            </FlexEndColumn>
          </RowWithTopMargin>
          <ReadOnlyRow
            className="gray-bg"
            leftHeader={titleLabel(
              getProgramCalculationLabels('total').replace(
                'USF',
                getUnit('area')
              )
            )}
            leftValue={
              hasSubtotalProgramUSF
                ? convertToUnit(totalProgramUSF, {
                    unitType: 'area',
                    formatter: (v) => asUsfOrZero(v, showDecimals),
                  })
                : NO_DATA_TO_SHOW
            }
            leftSign="+"
            middleValue={
              hasSubtotalProgramUSF
                ? convertToUnit(rentableLoadFactor, {
                    unitType: 'area',
                    formatter: (v) => asUsfOrZero(v, showDecimals),
                  })
                : NO_DATA_TO_SHOW
            }
            middleHeader={
              <>
                <div>Rentable</div>
                <div>Load Factor</div>
              </>
            }
            rightHeader={titleLabel(
              getRsfProgramCalculationLabels('total').replace(
                'RSF',
                getUnit('rentable-area')
              )
            )}
            rightValue={
              hasSubtotalProgramUSF
                ? convertToUnit(totalProgramRSF, {
                    unitType: 'area',
                  })
                : NO_DATA_TO_SHOW
            }
            testingIdPrefix="summary_calcs_tprsf_row"
            showDecimals={showDecimals}
          />
        </>
      )}
      {!isMacroCalculator &&
        (isTotalProgram || isDepartmentScenario ? (
          <UsfEditableRow
            leftHeader={titleLabel(
              getProgramCalculationLabels('totalAvailable').replace(
                'USF',
                getUnit('area')
              )
            )}
            middleHeader={titleLabel(
              getProgramCalculationLabels('total').replace(
                'USF',
                getUnit('area')
              )
            )}
            rightHeader={titleLabel(
              getProgramCalculationLabels('programDelta').replace(
                'USF',
                getUnit('area')
              )
            )}
            disabled={currentUserIsViewOnly}
            setEditFactor={setEditFactor}
            setShowFactorModal={setShowTPFactorModal}
            totalAvailableUsf={
              convertToUnit(totalAvailableUSF, { unitType: 'area' }) as number
            }
            totalProgramUsf={
              hasSubtotalProgramUSF
                ? (convertToUnit(totalProgramUSF, {
                    unitType: 'area',
                  }) as number)
                : NO_DATA_TO_SHOW
            }
            showInputModal={isTotalProgram}
            testingIdPrefix="summary_calcs_row5"
            showDecimals={showDecimals}
          />
        ) : (
          <ReadOnlyRow
            leftHeader={titleLabel(
              getProgramCalculationLabels('totalAvailable').replace(
                'USF',
                getUnit('area')
              )
            )}
            leftValue={convertToUnit(totalAvailableUSF, {
              unitType: 'area',
              formatter: (v) => asUsfOrZero(v, showDecimals),
            })}
            leftSign="-"
            leftSignStyle={{ fontSize: 22 }}
            middleHeader={titleLabel(
              getProgramCalculationLabels('total').replace(
                'USF',
                getUnit('area')
              )
            )}
            middleValue={
              hasSubtotalProgramUSF
                ? convertToUnit(totalProgramUSF, {
                    unitType: 'area',
                    formatter: (v) => asUsfOrZero(v, showDecimals),
                  })
                : NO_DATA_TO_SHOW
            }
            rightHeader={titleLabel(
              getProgramCalculationLabels('programDelta').replace(
                'USF',
                getUnit('area')
              )
            )}
            rightValue={convertToUnit(deltaProgramUSF, {
              unitType: 'area',
            })}
            testingIdPrefix="summary_calcs_row5"
            finalAnswer
            showDecimals={showDecimals}
          />
        ))}
      {rsfVisible && !isMacroCalculator && (
        <ReadOnlyRow
          className="gray-bg"
          leftHeader={titleLabel(
            getRsfProgramCalculationLabels('totalAvailable').replace(
              'RSF',
              getUnit('rentable-area')
            )
          )}
          leftValue={
            hasSubtotalProgramUSF
              ? convertToUnit(totalAvailableRSF, {
                  unitType: 'area',
                  formatter: (v) => asUsfOrZero(v, showDecimals),
                })
              : NO_DATA_TO_SHOW
          }
          leftSign="-"
          leftSignStyle={{
            fontSize: 22,
          }}
          middleHeader={titleLabel(
            getRsfProgramCalculationLabels('total').replace(
              'RSF',
              getUnit('rentable-area')
            )
          )}
          middleValue={
            hasSubtotalProgramUSF
              ? convertToUnit(totalProgramRSF, {
                  unitType: 'area',
                  formatter: (v) => asUsfOrZero(v, showDecimals),
                })
              : NO_DATA_TO_SHOW
          }
          rightHeader={titleLabel(
            getRsfProgramCalculationLabels('programDelta').replace(
              'RSF',
              getUnit('rentable-area')
            )
          )}
          rightValue={
            hasSubtotalProgramUSF
              ? convertToUnit(deltaProgramRSF, {
                  unitType: 'area',
                })
              : NO_DATA_TO_SHOW
          }
          testingIdPrefix="summary_calcs_rsfdelta_row"
          finalAnswer
          showDecimals={showDecimals}
        />
      )}
      {!window.SPWExport && !inBrowserDownload && (
        <AddOrRemoveRsfBtnContainer>
          {rsfVisible ? (
            <SPWButtonIconAndText
              fullWidth={false}
              startIcon={<Img src="/images/trash-teal.svg" />}
              onClick={() =>
                rsfLoadFactorPercentage === 0
                  ? dispatch(setRsfLoadFactor(null))
                  : setRemoveRsfModalVisibility(true)
              }
              data-testid="remove_rsf_button"
            >
              REMOVE {getUnit('rentable-area')}
            </SPWButtonIconAndText>
          ) : (
            <SPWButtonIconAndText
              fullWidth={false}
              startIcon={<Img src="/images/add-button.svg" />}
              onClick={() => dispatch(setRsfLoadFactor(0))}
              data-testid="add_rsf_button"
            >
              ADD {getUnit('rentable-area')}
            </SPWButtonIconAndText>
          )}
        </AddOrRemoveRsfBtnContainer>
      )}
    </>
  ) : (
    <Row10TopPadding>No buildings added.</Row10TopPadding>
  );
};

export default USFBuildingCalculationsLeftPanel;
